import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { END } from 'redux-saga';
import upperFirst from 'lodash/upperFirst';
import Head from 'next/head';
import React from 'react';
import { connect } from 'react-redux';
import nextI18nextConfig from '~/../next-i18next.config';
import { wrapper } from '~/store';
import Config from '~/config';
import Layout from '~/containers/Layout';
import SearchResultContainer from '~/containers/SearchResultPage';
import {
  initOtherServiceSearchResults,
  searchPhoto,
} from '~/containers/SearchResultPage/actions';
import defaultPage, {
  defaultPageServerSideContext,
} from '~/../hocs/defaultPage';
import HrefLangs from '~/components/HrefLangs';
import { loadPinterest } from '~/utils/file-helper';

const mapStateToProps = (state) => ({
  photos: state.searchResult.search.photos,
  location: state.searchResult.search.query,
  total: state.searchResult.search.totalItem,
  totalPage: state.searchResult.search.totalPage,
  token: state.auth.token,
  hasPersonalize: state.searchResult.search.hasPersonalize,
  searchState: state.search,
});

// This page is use for optimize search url seo
@defaultPage()
@connect(mapStateToProps)
class PopularSearchPage extends React.Component {
  componentDidMount() {
    loadPinterest();
  }

  render() {
    const { t, lng, query, photos, total, location, totalPage } = this.props;

    const canonical = `${Config.getSiteURL()}/${
      Config.isIllustAC() ? 'free-illustrations-vectors/' : 'popular-page/'
    }${query.param}${parseInt(query.page) === 1 ? '' : `/${query.page}`}`;

    if (!location.page) {
      location.page = 1;
    }
    const page = parseInt(location.page, 10);

    const hrefPrev = `${canonical}/${page - 1}`;
    const hrefNext = `${canonical}/${page + 1}`;

    const data = photos[0] || {};
    let searchType = 'byText';
    let errorSearchPhoto;

    return (
      <Layout>
        <Head>
          <HrefLangs
            pagePath={`/${
              Config.isIllustAC()
                ? 'free-illustrations-vectors/'
                : 'popular-page/'
            }${query.param}${
              parseInt(query.page) === 1 ? '' : `/${query.page}`
            }`}
            lng={lng}
          />
          <title>
            {t('ill_popular_search_title', {
              keyword: upperFirst(query.keyword),
            })}
          </title>
          {page === 1 ? '' : <link rel="prev" href={hrefPrev} />}
          {page >= totalPage ? '' : <link rel="next" href={hrefNext} />}
          <meta
            id="meta-keywords"
            name="keywords"
            content={t(
              Config.getPrefixContent('meta_keywords_search_page_seo'),
              {
                keywords: upperFirst(query.keyword),
              },
            )}
          />
          <meta
            id="meta-description"
            name="description"
            content={t('ill_popular_search_description', {
              keyword: query.keyword,
            })}
          />
          <meta property="og:image" content={data.thumbnail} />
          <meta
            property="og:title"
            content={t('ill_popular_search_og_title', {
              keyword: query.keyword,
            })}
          />
          <meta
            property="og:description"
            content={t('ill_popular_search_og_description', {
              keyword: query.keyword,
            })}
          />
          {lng === 'en' && <meta property="og:locale" content={'en_US'} />}
          {lng !== 'en' && (
            <meta
              property="og:locale"
              content={lng + '_' + lng?.toUpperCase()}
            />
          )}

          <meta property="og:type" content="article" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={`${canonical}`} />
          <meta
            name="twitter:title"
            content={t(Config.getPrefixContent('title_search_page_seo'), {
              keywords: upperFirst(query.keyword).replace(/\+/g, ' ') || '',
            })}
          />
          <meta
            name="twitter:description"
            content={t(
              Config.getPrefixContent('meta_description_search_page_seo'),
              {
                keywords: query.keyword,
                total,
              },
            )}
          />
          <meta name="twitter:image" content={data.path} />
          <meta name="twitter:site" content={`${Config.getSiteURL()}`} />
          <meta name="twitter:creator" content={`@${Config.getName()}`} />
        </Head>
        <SearchResultContainer
          t={t}
          lng={lng}
          searchType={searchType}
          errorSearchPhoto={errorSearchPhoto}
          isFromPopularPage={true}
          pathSlug={query.param}
        />
      </Layout>
    );
  }
}

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async (ctx) => {
    const i18nProps = await serverSideTranslations(
      ctx.locale || process.env.LANGUAGE,
      ['common'],
      nextI18nextConfig,
    );

    const {
      token,
      profile,
      accountType,
      isAuthenticated,
      currentLanguage,
      clientIp,
      api,
      sessionId,
      ssoToken,
      query,
    } = await defaultPageServerSideContext({
      ...ctx,
      store,
    });

    //convert param to keyword for search and add some properties
    query.keyword = query.param.replace(/-/g, ' ');
    query.color = 'all';
    query.modelCount = '-2';
    query.shape = 'all';
    query.orderBy = 'all_images';

    const searchQuery = {
      ...query,
      language: currentLanguage,
      token: store.getState().auth.token,
      clientIp,
      is_search_personalize:
        Config.isPhotoAC() &&
        store.getState().auth.isAuthenticated &&
        store.getState().searchResult.search.hasPersonalize
          ? '1'
          : '0',
    };

    store.dispatch(searchPhoto(searchQuery, api));
    store.dispatch(initOtherServiceSearchResults());

    // end the saga
    store.dispatch(END);
    await store.sagaTask.toPromise();

    if (store.getState().searchResult?.search?.photos.length === 0) {
      ctx.res.statusCode = 404;
    }

    return {
      props: {
        ...i18nProps,
        token,
        profile,
        accountType,
        isAuthenticated,
        currentLanguage,
        sessionId,
        ssoToken,
        query,
      },
    };
  },
);

export default PopularSearchPage;
